import { Table } from 'antd'
import { ColumnsType } from 'antd/es/table/interface'
import { Breakpoint } from 'antd/lib/_util/responsiveObserver'
import React, { FunctionComponent, useCallback, useMemo } from 'react'

import { PROPERTY_TYPES } from '../../../common/drmTypes'
import { PROPERTY_TAB_KEYS } from '../../../common/propertyDetailTypes'
import {
  MonitFilter,
  usePriceEstimateTableQuery,
} from '../../../graphql/generated'
import { useAppSelector } from '../../../redux/hooks'
import {
  labelSorter,
  numberSorter,
  stringSorter,
  utcDateSorter,
} from '../../utils/generalTableHelpers'
import { LabelConnectionFragment } from '../../utils/labelsTypes'
import {
  areaPriceEstimateTitlePrefix,
  buildingPriceEstimateTitlePrefix,
  tablePagination,
  unitPriceEstimateTitlePrefix,
} from '../../utils/layoutConst'
import { useColumnFilters } from '../../utils/useColumnFilters'
import { useColumnOptionFilter } from '../../utils/useColumnOptionFilter'
import { useTableLocale } from '../../utils/useTableLocale'
import { Day, Min } from '../DateFormat/DateFormat'
import { Money } from '../Money/Money'
import { PriceEstimatePdfButton } from '../PriceEstimatePdfButton/PriceEstimatePdfButton'
import { PropertyDetailLink } from '../PropertyDetailLink/PropertyDetailLink'
import { TableCellLabels } from '../TableCellLabels/TableCellLabels'
import { TableSummaryExport } from '../TableSummaryExport/TableSummaryExport'

export interface PriceEstimateTable {
  cenaM2Nab: string | number
  cenaM2Real: string | number
  cenaNab: string | number
  cenaReal: string | number
  createdAt: Date
  estimateToDate: Date
  id: string
  propertyId: string
  propertyType: number
  title: string
  labels?: LabelConnectionFragment
  note?: string
}

export interface PriceEstimateTableProps {
  monitFilter?: MonitFilter
}

const getType = (propertyType: number) => {
  return propertyType === 0
    ? PROPERTY_TYPES.AREA
    : propertyType === 1
    ? PROPERTY_TYPES.BUILDING
    : PROPERTY_TYPES.UNIT
}

const propertyType = [
  { text: 'Jednotka', value: 2 },
  { text: 'Parcela', value: 0 },
  { text: 'Budova', value: 1 },
]

const tableId = 'priceEstimate-table'
const scrollX = { x: 1300 }
const moneyRenderer = (value: string | number) => (
  <Money
    noData="---"
    amount={value}
    symbol={'Kč'}
    decimalNumbers={0}
    grayscale
  />
)

export const PriceEstimateTable: FunctionComponent<PriceEstimateTableProps> =
  props => {
    const tenantId = useAppSelector(state => state.myProfile.tenantId)

    const { searchFilter, dateFilter, moneyFilter, labelFilter } =
      useColumnFilters<PriceEstimateTable>()

    const { getColumnOptionFilter } = useColumnOptionFilter<PriceEstimateTable>(
      undefined,
      propertyType
    )
    const [queryResult, refetch] = usePriceEstimateTableQuery({
      variables: { accountId: tenantId, filter: props.monitFilter },
    })

    const dataSource = useMemo(() => {
      return (queryResult.data?.allMonits?.nodes as PriceEstimateTable[]) || []
    }, [queryResult.data?.allMonits?.nodes])

    const columns = useMemo<ColumnsType<PriceEstimateTable>>(
      () => [
        {
          title: 'ID',
          dataIndex: 'id',
          responsive: ['' as Breakpoint],
        },
        {
          title: 'Vytvořeno',
          dataIndex: 'createdAt',
          defaultSortOrder: 'descend',
          sorter: utcDateSorter('createdAt'),
          ...dateFilter('createdAt'),
          render: createdAt => <Min>{createdAt}</Min>,
        },
        {
          title: 'Odhad k datu',
          dataIndex: 'estimateToDate',
          sorter: utcDateSorter('estimateToDate'),
          ...dateFilter('estimateToDate'),
          render: estimateToDate => <Day>{estimateToDate}</Day>,
        },
        {
          title: 'Štítek',
          dataIndex: 'labels',
          sorter: labelSorter('labels'),
          ...labelFilter('labels'),
          render: (labels, record) => (
            <TableCellLabels
              labels={labels}
              labelEntityType="monitId"
              recordId={record.id}
              refetch={refetch}
              modalTitle={record.title}
            />
          ),
        },
        {
          title: 'Typ',
          dataIndex: 'propertyType',
          className: 'block first-letter:uppercase',
          sorter: numberSorter('propertyType'),
          ...getColumnOptionFilter('propertyType'),
          render: propertyType => getType(propertyType),
        },
        {
          title: 'Název',
          dataIndex: 'title',
          sorter: stringSorter('title'),
          ...searchFilter('title', 'Hledat v názvu'),
          render: (title, record) => {
            const text = title
              ?.replace(buildingPriceEstimateTitlePrefix, '')
              .replace(areaPriceEstimateTitlePrefix, '')
              .replace(unitPriceEstimateTitlePrefix, '')

            return (
              <PropertyDetailLink
                propertyId={record.propertyId}
                propertyType={getType(record.propertyType)}
                drawerTarget
                text={text}
                tab={PROPERTY_TAB_KEYS.PRICE_ESTIMATE}
              />
            )
          },
        },
        {
          title: 'Poznámka',
          dataIndex: 'note',
          ...searchFilter('note', 'Hledat v poznámce'),
        },
        {
          title: 'Nabídková cena\u00A0m²',
          dataIndex: 'cenaM2Nab',
          className: '!text-right',
          sorter: numberSorter('cenaM2Nab'),
          ...moneyFilter('cenaM2Nab'),
          render: moneyRenderer,
        },
        {
          title: 'Prodejní cena\u00A0m²',
          dataIndex: 'cenaM2Real',
          className: '!text-right',
          sorter: numberSorter('cenaM2Real'),
          ...moneyFilter('cenaM2Real'),
          render: moneyRenderer,
        },
        {
          title: 'Nabídková cena',
          dataIndex: 'cenaNab',
          className: '!text-right',
          sorter: numberSorter('cenaNab'),
          ...moneyFilter('cenaNab'),
          render: moneyRenderer,
        },
        {
          title: 'Prodejní cena',
          dataIndex: 'cenaReal',
          className: '!text-right',
          sorter: numberSorter('cenaReal'),
          ...moneyFilter('cenaReal'),
          render: moneyRenderer,
        },
        {
          title: 'Akce',
          dataIndex: 'actions',
          className: 'xl:w-52 max-w-[210px] !text-right',
          render: (_, record) => (
            <div className="space-x-2 text-right">
              <PropertyDetailLink
                propertyId={record.propertyId}
                propertyType={getType(record.propertyType)}
                text="zobrazit detail"
                tab={PROPERTY_TAB_KEYS.PRICE_ESTIMATE}
              />
              <PriceEstimatePdfButton
                buttonProps={{ ghost: true, size: 'small' }}
                id={record.id}
              />
            </div>
          ),
        },
      ],
      [
        dateFilter,
        getColumnOptionFilter,
        labelFilter,
        moneyFilter,
        refetch,
        searchFilter,
      ]
    )

    const summary = useCallback(
      () => (
        <TableSummaryExport
          total={dataSource?.length || 0}
          colSpan={9}
          colSpanAction={2}
          url="monit/monitXlsx"
          fileName="Cenove-odhady"
          dataSource={dataSource || []}
        />
      ),
      [dataSource]
    )

    return (
      <Table
        id={tableId}
        rowKey="id"
        rowClassName="animate-fadeIn"
        size="small"
        scroll={scrollX}
        dataSource={dataSource}
        columns={columns}
        loading={queryResult.fetching}
        locale={useTableLocale(queryResult.fetching, 10)}
        summary={summary}
        pagination={tablePagination}
      />
    )
  }
